import { useEffect, useState } from "react";
import { api, formatRupiah, formatDate } from "@/lib/api";
import { TrendingUp, TrendingDown, AlertTriangle, Calendar, Target, FileClock } from "lucide-react";
import { Link } from "react-router-dom";
import { SpkCard, PhotoViewer } from "@/components/SpkPanel";

function MySpk() {
  const [list, setList] = useState([]);
  const [viewing, setViewing] = useState(null);
  const load = () => api.get("/spk").then(r => setList(r.data.filter(s => s.review_status !== "Disetujui")));
  useEffect(() => { load(); }, []);
  async function setStatus(s, status) { await api.patch(`/spk/${s.id}`, { status }); load(); }
  return (
    <div className="card-editorial p-5" data-testid="dash-my-spk">
      <h2 className="font-serif-display text-xl font-semibold mb-4">SPK Aktif</h2>
      <div className="grid md:grid-cols-2 gap-3">
        {list.map(s => <SpkCard key={s.id} s={s} onStatus={setStatus} onOpen={setViewing} onChanged={load} showProject />)}
      </div>
      {list.length === 0 && <p className="text-sm text-[#8A999A]">Tidak ada SPK aktif</p>}
      <PhotoViewer path={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

function TeamDashboard({ d }) {
  return (
    <div className="p-4 sm:p-8 space-y-6" data-testid="team-dashboard">
      <header>
        <h1 className="text-2xl sm:text-3xl font-serif-display font-bold">Dashboard Tim</h1>
        <p className="text-sm text-[#536263]">Jadwal event &amp; tugas lapangan</p>
        <div className="mt-2 h-px w-12 bg-[#B87333]"></div>
      </header>
      <div className="grid grid-cols-2 gap-4">
        <div className="card-editorial p-5" data-testid="team-active-projects">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-[#B87333]">Project Aktif</div>
          <div className="font-serif-display text-3xl font-bold">{d.active_projects}</div>
        </div>
        <div className="card-editorial p-5" data-testid="team-pending-expenses">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-[#B87333]">Pengajuan Saya Pending</div>
          <div className="font-serif-display text-3xl font-bold">{d.pending_my_expenses}</div>
        </div>
      </div>
      <MySpk />
      <div className="card-editorial p-5">
        <h2 className="font-serif-display text-xl font-semibold mb-4">Event 30 Hari Ke Depan</h2>
        <div className="space-y-2" data-testid="dash-upcoming-events">
          {d.upcoming_events.length === 0 && <p className="text-sm text-[#8A999A]">Tidak ada event mendatang</p>}
          {d.upcoming_events.map(e => (
            <Link to={`/projects/${e.id}`} key={e.id} className="flex items-center justify-between p-3 bg-[#FAF7F2] rounded-lg">
              <div className="flex items-center gap-3 min-w-0">
                <Calendar size={18} className="text-[#0F5257] shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{e.name}</div>
                  <div className="text-xs text-[#8A999A]">{formatDate(e.event_date)} · {e.status}</div>
                </div>
              </div>
              <span className="chip bg-[#F7EFE8] text-[#B87333] shrink-0">{e.segment}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/dashboard").then(r => setD(r.data)).catch(() => {});
  }, []);
  if (!d) return <div className="p-6">Memuat...</div>;
  if (d.restricted) return <TeamDashboard d={d} />;

  const Card = ({ title, value, hint, icon: Icon, testid, tone = "default" }) => (
    <div data-testid={testid} className="card-editorial p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-[#B87333]">{title}</div>
        <Icon size={16} className="text-[#8A999A]" />
      </div>
      <div className={`font-serif-display text-2xl sm:text-3xl font-bold ${tone === "danger" ? "text-red-700" : "text-[#1A2627]"}`}>{value}</div>
      {hint && <div className="text-xs text-[#8A999A] mt-1">{hint}</div>}
    </div>
  );

  return (
    <div className="p-4 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif-display font-bold">Dashboard Owner</h1>
          <p className="text-sm text-[#536263]">Ringkasan operasional &amp; keuangan hari ini</p>
          <div className="mt-2 h-px w-12 bg-[#B87333]"></div>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card title="Kas Masuk Bulan Ini" value={formatRupiah(d.cash_in_month)} icon={TrendingUp} testid="dash-cash-in" />
        <Card title="Kas Keluar Bulan Ini" value={formatRupiah(d.cash_out_month)} icon={TrendingDown} testid="dash-cash-out" />
        <Card title="Total Piutang" value={formatRupiah(d.piutang)} icon={FileClock} testid="dash-piutang" />
        <Card title="Pengajuan Pending" value={d.pending_expense_approvals} icon={AlertTriangle} testid="dash-pending"
          hint={`${d.low_margin_quotations} quotation margin rendah`} tone={d.pending_expense_approvals > 0 ? "danger" : "default"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card-editorial p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif-display text-xl font-semibold">Event 30 Hari Ke Depan</h2>
            <Link to="/projects" className="text-xs text-[#B87333]">Lihat semua →</Link>
          </div>
          <div className="space-y-2" data-testid="dash-upcoming-events">
            {d.upcoming_events.length === 0 && <p className="text-sm text-[#8A999A]">Tidak ada event mendatang</p>}
            {d.upcoming_events.map(e => (
              <div key={e.id} className="flex items-center justify-between p-3 bg-[#FAF7F2] rounded-lg">
                <div className="flex items-center gap-3 min-w-0">
                  <Calendar size={18} className="text-[#0F5257] shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{e.name}</div>
                    <div className="text-xs text-[#8A999A]">{formatDate(e.event_date)}</div>
                  </div>
                </div>
                <span className="chip bg-[#F7EFE8] text-[#B87333] shrink-0">{e.segment}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card-editorial p-5">
          <h2 className="font-serif-display text-xl font-semibold mb-4">Margin per Segmen</h2>
          <div className="space-y-3" data-testid="dash-margin-segments">
            {Object.entries(d.margin_by_segment || {}).map(([k, v]) => (
              <div key={k}>
                <div className="flex justify-between text-sm mb-1"><span>{k}</span><span className="font-mono-num font-semibold">{v}%</span></div>
                <div className="h-2 bg-[#F2EEE7] rounded">
                  <div className="h-2 rounded" style={{ width: `${Math.min(100, Math.max(0, v))}%`,
                    background: v >= 20 ? "#0F5257" : v >= 15 ? "#B87333" : "#C53030" }}></div>
                </div>
              </div>
            ))}
            {Object.keys(d.margin_by_segment || {}).length === 0 && <p className="text-sm text-[#8A999A]">Belum ada quotation</p>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-editorial p-5">
          <h2 className="font-serif-display text-xl font-semibold mb-4 flex items-center gap-2"><Target size={18} />Funnel Penjualan</h2>
          <div className="space-y-2" data-testid="dash-funnel">
            {Object.entries(d.funnel).map(([stage, count]) => (
              <div key={stage} className="flex items-center gap-3">
                <div className="w-28 text-xs text-[#536263]">{stage}</div>
                <div className="flex-1 h-6 bg-[#F2EEE7] rounded relative overflow-hidden">
                  <div className="h-6 bg-[#0F5257]" style={{ width: `${Math.min(100, count * 15)}%` }}></div>
                </div>
                <div className="w-8 text-right text-sm font-mono-num font-semibold">{count}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-editorial p-5">
          <h2 className="font-serif-display text-xl font-semibold mb-4 flex items-center gap-2 text-red-700"><AlertTriangle size={18} />Peringatan</h2>
          <div className="space-y-2" data-testid="dash-alerts">
            {(d.alerts || []).length === 0 && <p className="text-sm text-[#8A999A]">Tidak ada peringatan aktif</p>}
            {(d.alerts || []).map(a => (
              <div key={a.id} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="text-sm text-red-900">{a.message}</div>
                <div className="text-[10px] text-red-700 mt-1 uppercase tracking-wider">{a.type}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
